// Workstream: growth. Occasion landing pages for search: each has its own copy, headings and links
// into the product. Titles and descriptions live in apps/shared/pages.ts.
import type { ReactNode } from 'react';
import { useScrollToTop } from './useScrollToTop';
import { Link } from 'react-router';
import { pageMeta, pageSlug } from '@shared/pages';
import { AnswerCard, PromptCard } from '../../components/Cards';
import { Footer, TopBar, useDocumentTitle } from '../../components/Chrome';

interface SeoSection {
  heading: string;
  body: ReactNode;
}

interface SeoPageProps {
  path: string;
  kicker: string;
  lede: ReactNode;
  sections: SeoSection[];
  secondary: { to: string; label: string };
  closing: { heading: string; text: string };
}

function SeoPage({ path, kicker, lede, sections, secondary, closing }: SeoPageProps) {
  const meta = pageMeta(path)!;
  const slug = pageSlug(path);
  useDocumentTitle(meta.title);
  useScrollToTop();
  return (
    <>
      <TopBar />
      <main className="page mk">
        <header className="mk-hero">
          <p className="mk-kicker">{kicker}</p>
          <h1>{meta.headline}</h1>
          <p className="mk-lede">{lede}</p>
          <div className="mk-actions">
            <Link className="btn btn-primary btn-lg" to="/" data-cta={`${slug}_start`}>
              Start a free game
            </Link>
            <Link className="btn btn-outline btn-lg" to={secondary.to} data-cta={`${slug}_secondary`}>
              {secondary.label}
            </Link>
          </div>
        </header>

        <div className="demo-cards mk-sample" aria-label="A sample round">
          <PromptCard card={{ id: `${slug}-p`, type: 'prompt', text: meta.prompt, pick: 1 }} />
          <AnswerCard card={{ id: `${slug}-a`, type: 'answer', text: meta.answer, pick: 1 }} />
        </div>

        {sections.map((s) => (
          <section key={s.heading} className="mk-section">
            <h2>{s.heading}</h2>
            {s.body}
          </section>
        ))}

        <section className="mk-closing">
          <h2>{closing.heading}</h2>
          <p>{closing.text}</p>
          <Link className="btn btn-primary btn-lg" to="/" data-cta={`${slug}_closing_start`}>
            Start a free game
          </Link>
        </section>
      </main>
      <Footer />
    </>
  );
}

// ------------------------------------------------------------------ Diwali

export function DiwaliPage() {
  return (
    <SeoPage
      path="/diwali-party-games"
      kicker="Diwali night, sorted"
      lede={
        <>
          The puja is done, the mithai is out, and three generations are in one living room. Half of them are on their phones anyway. CompleteMasti turns
          those phones into the game: a fill-in-the-blank card game about aunties, rishtas, filmy dialogues and everything else a desi family laughs about.
        </>
      }
      secondary={{ to: '/games', label: 'More Diwali games' }}
      sections={[
        {
          heading: 'Why a card game works on Diwali night',
          body: (
            <>
              <p>
                Diwali parties have a problem most party games can't solve. Nani wants to play, the eight-year-old wants to play, and the cousin who just
                flew in from Toronto wants to play, but they don't laugh at the same things. CompleteMasti gives everyone the same prompt and lets each person
                answer in their own style. Dadi playing "Sharma ji ka beta" at exactly the right moment gets the biggest laugh of the night.
              </p>
              <p>
                Rounds take about two minutes, so people can drift to the kitchen for more gulab jamun and come back without breaking anything. If a phone
                locks or someone refreshes, they keep their seat, their cards and their score.
              </p>
            </>
          ),
        },
        {
          heading: 'Set it up in two minutes',
          body: (
            <ol className="steps">
              <li>
                <div>
                  <strong>Start a room</strong> on <Link to="/">the home page</Link>. You get a 4-letter code.
                </div>
              </li>
              <li>
                <div>
                  <strong>Drop the invite in the family WhatsApp group.</strong> Everyone taps it and types their name. No app, no sign-up.
                </div>
              </li>
              <li>
                <div>
                  <strong>Put TV mode on the big screen</strong> so the elders can follow along without squinting at a phone.
                </div>
              </li>
              <li>
                <div>
                  <strong>Choose your packs</strong> and tap start. The judge reads the prompt out loud, or taps 🔊 to have the phone read it.
                </div>
              </li>
            </ol>
          ),
        },
        {
          heading: 'Pick the right packs for your crowd',
          body: (
            <>
              <p>
                <strong>Classic</strong> and <strong>Filmy Dialogues</strong> are on by default and work for almost every family. If there's a wedding
                coming up, add <strong>Shaadi Season</strong>. Keep <strong>Besharam</strong> for after the elders go to bed; it's 18+ and stays off unless
                the host turns it on. You can see a few cards from each pack on the <Link to="/packs">packs page</Link>.
              </p>
              <p>
                For the best Diwali of all, <Link to="/packs/new">make your own pack</Link> the night before. Try prompts like "The first person to fall
                asleep after the puja is ___" or "Every year, Chachu insists on bursting ___." Share the pack code and add it from the lobby.
              </p>
            </>
          ),
        },
        {
          heading: 'Tips from our own family Diwalis',
          body: (
            <ul>
              <li>Seat each grandparent next to a cousin who can help with the phone for the first round. By round three they won't need help.</li>
              <li>Turn on "everyone votes" if the group is bigger than ten, so nobody sits out a round as the judge.</li>
              <li>Set the target to 5 points before dinner and 10 after. Short games keep the energy up.</li>
              <li>
                For the second game, open <strong>Extra masti</strong> in the lobby and play Kids vs Parents as teams. Add a Sabse Bekaar round, where the
                worst card wins, and watch the elders get competitive.
              </li>
              <li>The winner gets the last kaju katli. This is non-negotiable.</li>
            </ul>
          ),
        },
        {
          heading: 'More games for the same night',
          body: (
            <p>
              Not everyone has a phone handy? Try <Link to="/games/one-phone">one-phone mode</Link> and pass a single phone around. Our{' '}
              <Link to="/games/charades">Dumb Charades</Link> deck has Bollywood films, songs and TV shows sorted by difficulty, and{' '}
              <Link to="/games/dialogue">Finish the Dialogue</Link> turns famous filmy lines into a quiz.
            </p>
          ),
        },
      ]}
      closing={{ heading: 'Happy Diwali from Priten and Chandani', text: 'Grab the family, start a room, and let the roasting begin. It is free to play.' }}
    />
  );
}

// ------------------------------------------------------------------ Sangeet

export function SangeetPage() {
  return (
    <SeoPage
      path="/sangeet-games"
      kicker="For sangeets and mehndis"
      lede={
        <>
          Two families, one hall, and a lot of people who have only met at the roka. CompleteMasti is the icebreaker that gets the groom's side and the
          bride's side laughing together, on their own phones or on the big screen between performances.
        </>
      }
      secondary={{ to: '/events', label: 'Event host kit' }}
      sections={[
        {
          heading: 'The sangeet icebreaker that actually works',
          body: (
            <>
              <p>
                Most sangeet games need a microphone, a confident MC and volunteers who are willing to embarrass themselves. CompleteMasti only needs a
                room code. Guests join from their seats, everyone plays a card for each prompt, and the funniest answer goes up on the screen. The shy
                cousins play just as hard as the loud mamaji, because nobody has to stand up.
              </p>
              <p>
                It also fills the awkward gaps. The choreographed dance is running late? Play three quick rounds. The caterer needs ten more minutes? Play
                three more.
              </p>
            </>
          ),
        },
        {
          heading: 'Make it about the couple',
          body: (
            <>
              <p>
                The best sangeet games are about the two people getting married. <Link to="/packs/new">Make a custom pack</Link> with prompts only your
                families will get:
              </p>
              <ul>
                <li>"The real reason Riya said yes: ___."</li>
                <li>"Arjun's mother has already planned ___ for the first anniversary."</li>
                <li>"The one thing the two families will never agree on: ___."</li>
              </ul>
              <p>
                Mix your pack with our <strong>Shaadi Season</strong> pack, which is full of baraat, band baaja and wedding-buffet jokes. Leave the 18+{' '}
                <strong>Besharam</strong> pack for the bachelor party.
              </p>
            </>
          ),
        },
        {
          heading: 'Running it on the big screen',
          body: (
            <ol className="steps">
              <li>
                <div>
                  <strong>Connect a laptop to the projector</strong> and open TV mode for your room. It shows a QR code so guests can join by pointing
                  their camera.
                </div>
              </li>
              <li>
                <div>
                  <strong>Switch the room to "everyone votes"</strong> so the whole hall picks the winner and nobody has to judge their future in-laws.
                </div>
              </li>
              <li>
                <div>
                  <strong>Keep it to 15 or 20 minutes.</strong> Set a round timer so things keep moving, and have the MC read each prompt out loud.
                </div>
              </li>
            </ol>
          ),
        },
        {
          heading: 'Smaller functions work too',
          body: (
            <p>
              Not every sangeet is a banquet hall with a DJ. For a mehndi at home with twenty cousins, skip the projector: everyone plays on their own phone
              and the judge reads each prompt out loud while the mehndi dries. Anyone whose hands are covered can pair up with a neighbour, or you can pass a
              single phone around in <Link to="/games/one-phone">one-phone mode</Link>. The bride gets to judge the first round, obviously.
            </p>
          ),
        },
        {
          heading: 'Want us to set it up for you?',
          body: (
            <p>
              For bigger weddings we offer an <Link to="/events">event host kit</Link>: we help write a custom pack about the couple, send a host script for
              your MC, and can print boxed decks for the tables or as favours. Digital play is always free; ask us about the extras.
            </p>
          ),
        },
      ]}
      closing={{ heading: 'Mubarak ho!', text: 'Start a room now to try it out before the big night. Two minutes with your cousins is all the testing you need.' }}
    />
  );
}

// ------------------------------------------------------------------ Family Zoom

export function FamilyZoomPage() {
  return (
    <SeoPage
      path="/family-zoom-games"
      kicker="For families across time zones"
      lede={
        <>
          Your parents are in Pune, your sister is in London, your cousins are in Houston and Nana ji only knows how to answer the video call, not how to
          end it. CompleteMasti gives the whole call something to do together besides asking about your salary.
        </>
      }
      secondary={{ to: '/how-to-play', label: 'How to play' }}
      sections={[
        {
          heading: 'How it works on a video call',
          body: (
            <>
              <p>
                Everyone stays on Zoom, Google Meet or WhatsApp video for the talking and laughing. The game itself runs in the browser on each person's
                phone. One person starts a room and pastes the link in the chat; everyone taps it, types a name and they're in. There's nothing to
                install, and it works the same in Chennai and Chicago.
              </p>
              <p>
                On a call, we recommend <strong>everyone votes</strong> mode. Each round, all players pick a card and then vote for their favourite (you can't
                vote for your own). Nobody sits out as judge, and nobody has to manage the screen.
              </p>
            </>
          ),
        },
        {
          heading: 'Set up the call for success',
          body: (
            <ul>
              <li>
                <strong>Share the TV screen.</strong> One person opens TV mode on a laptop and shares it, so the prompt and the winning card are big on
                everyone's call window.
              </li>
              <li>
                <strong>Use two devices if you can.</strong> The laptop for the video, the phone for your cards. Elders on a single tablet can play from the
                same tablet in a second browser tab.
              </li>
              <li>
                <strong>Let the phone read aloud.</strong> Tap 🔊 on a card and it reads the prompt, which helps grandparents with small screens and anyone
                with a bad connection.
              </li>
              <li>
                <strong>Turn on the round timer</strong> so a frozen video doesn't stall the game.
              </li>
            </ul>
          ),
        },
        {
          heading: 'Finding a time that works for everyone',
          body: (
            <p>
              A Sunday morning in North America is Sunday evening in India, Pakistan and Bangladesh, and lunchtime in the UK. That window is the sweet spot
              for most desi families. A full game to 7 points takes about 25 minutes, which is just long enough before somebody's chai goes cold.
            </p>
          ),
        },
        {
          heading: 'When not everyone gets the reference',
          body: (
            <p>
              Hindi and Urdu lines show a short English translation under the card, and the (i) button explains the film, song or custom behind it. So the
              cousins born abroad can finally understand why Papa laughs every time someone says "Mogambo khush hua." You can turn translations off from the
              menu if your crowd doesn't need them.
            </p>
          ),
        },
        {
          heading: 'Make it personal',
          body: (
            <p>
              <Link to="/packs/new">Make a family pack</Link> full of inside jokes: the uncle who forwards everything, the time someone got lost at the
              airport, Mummy's famous voice notes. It takes ten minutes, and it's the part everyone remembers.
            </p>
          ),
        },
      ]}
      closing={{ heading: 'Call the family, not just the group chat', text: 'Start a room, paste the link in the video call chat, and play your first round in under a minute.' }}
    />
  );
}

// ------------------------------------------------------------------ Desi party games

export function DesiPartyPage() {
  return (
    <SeoPage
      path="/desi-party-games"
      kicker="For desi game nights"
      lede={
        <>
          Antakshari runs out of songs. Dumb Charades runs out of films nobody has done. CompleteMasti is the desi party game that never runs out: more than
          a thousand cards about aunties, rishtas, filmy dialogues, chai, cricket and growing up brown, played on everyone's phones.
        </>
      }
      secondary={{ to: '/packs', label: 'Browse the packs' }}
      sections={[
        {
          heading: 'What kind of game is it?',
          body: (
            <>
              <p>
                If you've played Cards Against Humanity or Apples to Apples, you already know how it works. Each round has a prompt card with a blank. Everyone
                plays their funniest answer card from their hand, and the judge picks a winner. The difference is that every card was written for us: the
                jokes are about the rishta aunty, the Maggi at 2am, the dad who negotiates at Costco and the dialogues from films we all grew up on.
              </p>
              <p>
                It runs in the browser, so there's nothing to download. Someone starts a room, everyone joins with a 4-letter code, and you're playing.
              </p>
            </>
          ),
        },
        {
          heading: 'Good for every kind of desi party',
          body: (
            <ul>
              <li>
                <strong>Dorm and hostel nights:</strong> 3 to 16 players, with a round timer so it keeps moving.
              </li>
              <li>
                <strong>Student association socials:</strong> put TV mode on the projector and let the whole room vote.
              </li>
              <li>
                <strong>Dinner parties:</strong> play a few rounds between courses. People can drop in and out without breaking the game.
              </li>
              <li>
                <strong>Family get-togethers:</strong> the Classic and Filmy packs are safe for the whole khandaan.
              </li>
            </ul>
          ),
        },
        {
          heading: 'Hosting tips',
          body: (
            <ul>
              <li>Start the room before people arrive and pin the code on the TV, so latecomers can join between rounds.</li>
              <li>With more than eight players, switch to "everyone votes" so nobody waits a whole round as judge.</li>
              <li>Set the target to 7 points for a game that lasts about half an hour, then play again with the loser as host.</li>
              <li>Mix it up from the lobby's Extra masti settings: teams, Guess who played it, and rounds where you write the prompt yourselves.</li>
            </ul>
          ),
        },
        {
          heading: 'For the grown-ups only crowd',
          body: (
            <p>
              Once the parents have left, the host can switch on <strong>Besharam</strong>, our 18+ pack. It asks for a confirmation first and it never turns
              on by itself, so nobody ends up reading it to their Masi by accident.
            </p>
          ),
        },
        {
          heading: 'Make the jokes your own',
          body: (
            <p>
              Every player gets blank cards to write their own answers, and the best ones can be sent to us for the official deck. For a friend group with
              years of inside jokes, <Link to="/packs/new">make a custom pack</Link> and add it to any game by its code.
            </p>
          ),
        },
        {
          heading: 'More desi party games',
          body: (
            <p>
              On the same site you'll find <Link to="/games/charades">Dumb Charades</Link> with Bollywood titles sorted by difficulty,{' '}
              <Link to="/games/mat-bolna">Mat Bolna</Link> (describe the word without saying the forbidden ones) and{' '}
              <Link to="/games/dialogue">Finish the Dialogue</Link>, a filmy quiz for teams. Each runs on a single phone, so it works for a road trip too.
            </p>
          ),
        },
      ]}
      closing={{ heading: 'Game night starts here', text: 'Start a room and send the code to the group. First round in about a minute.' }}
    />
  );
}

// ------------------------------------------------------------------ Eid

export function EidPage() {
  return (
    <SeoPage
      path="/eid-party-games"
      kicker="Eid Mubarak"
      lede={
        <>
          After Eid prayers, the visits, the sheer khurma and the biryani, the whole family is finally in one place. CompleteMasti is a warm, family-friendly
          card game that keeps the cousins off their own screens and gets the elders laughing along with them.
        </>
      }
      secondary={{ to: '/games', label: 'One-phone games' }}
      sections={[
        {
          heading: 'A game the whole khandaan can play',
          body: (
            <>
              <p>
                Each round shows a prompt with a blank, and everyone plays their funniest answer from the cards on their phone. Rounds are quick, the rules
                take one minute to explain, and it works from about age ten upwards. The judge reads the prompt out loud, or taps 🔊 to let the phone read it.
              </p>
              <p>
                Urdu and Hindi lines come with a short English translation under the card, and the (i) button explains the film, song or custom behind a
                joke. Nobody gets left out because they didn't grow up with the reference.
              </p>
            </>
          ),
        },
        {
          heading: 'Keep it family friendly',
          body: (
            <p>
              The <strong>Classic</strong> and <strong>Filmy Dialogues</strong> packs are on by default and suit a family table. Our 18+ pack,{' '}
              <strong>Besharam</strong>, is always off unless the host deliberately turns it on, so you can hand the room code to the whole family group
              without worrying. See a sample of each pack on the <Link to="/packs">packs page</Link>.
            </p>
          ),
        },
        {
          heading: 'Ideas for Chaand Raat and Eid day',
          body: (
            <ul>
              <li>
                <strong>Chaand Raat:</strong> start a room while the mehndi dries. Nobody can hold a phone for long, so use{' '}
                <Link to="/games/one-phone">one-phone mode</Link> and let one person tap for the table.
              </li>
              <li>
                <strong>After lunch:</strong> put TV mode on the television and let the kids run the game for the elders.
              </li>
              <li>
                <strong>Eidi stakes:</strong> the winner of each game gets first pick from the dessert table, or a small bonus on their Eidi if the elders
                are feeling generous.
              </li>
              <li>
                <strong>Visiting relatives:</strong> new arrivals can join mid-game from the same code and get dealt straight in.
              </li>
            </ul>
          ),
        },
        {
          heading: 'Make an Eid pack',
          body: (
            <p>
              <Link to="/packs/new">Write your own pack</Link> with family jokes: "The aunty who asks about marriage before saying salaam is ___" or "This
              year, Abbu's Eidi speech will mention ___." Share the code and add it in the lobby. It takes about ten minutes and it's the part everyone
              remembers.
            </p>
          ),
        },
        {
          heading: 'Games for when the phones are charging',
          body: (
            <p>
              <Link to="/games/charades">Dumb Charades</Link> with film and song titles, <Link to="/games/mat-bolna">Mat Bolna</Link> and{' '}
              <Link to="/games/dialogue">Finish the Dialogue</Link> all run on a single phone with big text.
            </p>
          ),
        },
      ]}
      closing={{ heading: 'Eid Mubarak from Priten and Chandani', text: 'Start a free room and share the code with the family. Your first round takes about a minute.' }}
    />
  );
}

// ------------------------------------------------------------------ Baby shower / godh bharai

export function BabyShowerPage() {
  return (
    <SeoPage
      path="/baby-shower-games-desi"
      kicker="For godh bharai and desi baby showers"
      lede={
        <>
          Between the rituals, the blessings and the aunties predicting whether it's a boy or a girl from the shape of the bump, there's a lot of sitting
          around. CompleteMasti gives the guests something to do together that both the aunties and the friends from work will enjoy.
        </>
      }
      secondary={{ to: '/packs/new', label: 'Make a baby pack' }}
      sections={[
        {
          heading: 'Why it works for a godh bharai',
          body: (
            <>
              <p>
                Baby shower games usually mean guessing the bump size with a ribbon. Fun for five minutes. CompleteMasti is a fill-in-the-blank card game that
                everyone plays on their own phone. Each round has a prompt, everyone plays their funniest answer, and the mom-to-be can be the judge for the
                whole game. She deserves it.
              </p>
              <p>
                It works for a seemantham, a valaikappu, a godh bharai or a plain old baby shower, whether it's twelve people in a living room or sixty in a
                banquet hall with the game on a projector.
              </p>
            </>
          ),
        },
        {
          heading: 'Make it about the parents-to-be',
          body: (
            <>
              <p>
                The real fun is a custom pack. <Link to="/packs/new">Make one</Link> in ten minutes with prompts about the couple:
              </p>
              <ul>
                <li>"The baby will definitely inherit ___."</li>
                <li>"The first word will be ___."</li>
                <li>"Dad-to-be's plan for the 3am feed: ___."</li>
                <li>"The name Dadi has already chosen, whatever the parents say: ___."</li>
              </ul>
              <p>Mix it with the Classic pack so there are always good answers in everyone's hand.</p>
            </>
          ),
        },
        {
          heading: 'Aunties versus friends',
          body: (
            <p>
              Turn on <strong>everyone votes</strong> and watch the generations compete. The aunties will play the rishta jokes, the friends will play the
              office jokes, and the whole room decides. If some guests aren't comfortable with phones, seat them next to someone who is, or run{' '}
              <Link to="/games/one-phone">one-phone mode</Link> for their table. Hindi and Urdu cards show an English translation, so friends of every
              background can keep up.
            </p>
          ),
        },
        {
          heading: 'Fitting it around the rituals',
          body: (
            <p>
              A godh bharai has its own rhythm: the aarti, the blessings, the bangles, the gifts, the food. Play one short game while the guests arrive and
              another after lunch, when everyone is too full to move. Set the target to 5 points so each game takes about fifteen minutes, and let the
              elders bless the winner instead of handing out a prize.
            </p>
          ),
        },
        {
          heading: 'A keepsake for the new family',
          body: (
            <p>
              After the shower, your custom pack doesn't have to disappear. We can print it as a real boxed deck, shuffled in with the Classic cards, for the
              parents to keep and play at the baby's first birthday. See the <Link to="/shop">card decks</Link>, or <Link to="/events">get in touch</Link>{' '}
              for a bigger event.
            </p>
          ),
        },
      ]}
      closing={{ heading: 'Congratulations to the parents-to-be', text: 'Start a free room and send the code to the guests. The mom-to-be judges first.' }}
    />
  );
}
